/** Column definitions for the onboarding-invites grid (plan §15).
 *
 * All columns are `filterable: false` — filtering is the QuerySearch widget's (the page sets
 * `disableColumnFilter`). Sortable column FIELDS equal the backend ordering whitelist (id, label,
 * email, created_ts, expires_ts, claimed_ts, sent_ts, used_ts, completed_ts); everything else is
 * `sortable: false` (the dead-sort-header bug class). Cell popovers carry quick filters that
 * append an equality chip to the search. No column ever shows a code or a link.
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';

import type { InviteListItem } from '@/api/onboardingInvites';
import { CellActionsPopover, type CellAction } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { InviteActionsMenu, type RunInviteAction } from './InviteActions';
import type { InviteActionPerms } from './inviteActionRules';
import { ContextSummaryChips, InviteStatusChip, inviteSourceLabel, inviteStatusHint, userLabel } from './inviteFormat';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;
const dash = <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;

function copyAction(label: string, value: string): CellAction {
  return {
    key: 'copy',
    label,
    icon: <ContentCopyIcon fontSize="small" />,
    onClick: () => void navigator.clipboard?.writeText(value),
  };
}

export interface BuildColumnsOptions {
  /** Append an exact `ns=value` chip to the search. */
  onQuickFilter: (namespace: string, value: string) => void;
  /** Route to an invite's detail drawer (keeps the grid's query string). */
  detailPath: (id: number) => string;
  perms: InviteActionPerms;
  onRun: RunInviteAction;
}

export function buildColumns(opts: BuildColumnsOptions): GridColDef<InviteListItem>[] {
  const { onQuickFilter, detailPath, perms, onRun } = opts;
  const filter = (label: string, ns: string, value: string): CellAction => ({
    key: `filter-${ns}`,
    label,
    icon: <FilterAltIcon fontSize="small" />,
    onClick: () => onQuickFilter(ns, value),
  });

  return [
    {
      field: 'label',
      headerName: 'Label',
      flex: 1,
      minWidth: 200,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover auto title={p.row.label} actions={[copyAction('Copy label', p.row.label)]} sx={ellipsis}>
          <Box component="span">{p.row.label}</Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={inviteStatusHint(p.row.status)}
          actions={[filter('Filter by this status', 'status', p.row.status)]}
        >
          <InviteStatusChip status={p.row.status} />
        </CellActionsPopover>
      ),
    },
    {
      field: 'email',
      headerName: 'E-mail',
      width: 210,
      filterable: false,
      renderCell: (p) =>
        p.row.email ? (
          <CellActionsPopover
            auto
            title={p.row.email}
            actions={[copyAction('Copy e-mail', p.row.email), filter('Filter by this e-mail', 'email', p.row.email)]}
            sx={ellipsis}
          >
            <Box component="span">{p.row.email}</Box>
          </CellActionsPopover>
        ) : (
          <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: 13 }}>
            unbound
          </Box>
        ),
    },
    {
      field: 'context',
      headerName: 'Context',
      width: 240,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        [row.context_summary.restechs?.join(' ') ?? 'any', row.context_summary.promo_code ?? ''].join(' ').trim(),
      renderCell: (p) => {
        const s = p.row.context_summary;
        const actions: CellAction[] = [
          ...(s.restechs ?? []).map((r) => ({ ...filter(`Filter by booking system ${r}`, 'restech', r), key: `filter-restech-${r}` })),
          ...(s.promo_code ? [filter('Filter by this promo code', 'promo', s.promo_code)] : []),
        ];
        return actions.length ? (
          <CellActionsPopover auto actions={actions} sx={{ overflow: 'hidden' }}>
            <ContextSummaryChips summary={s} />
          </CellActionsPopover>
        ) : (
          <ContextSummaryChips summary={s} />
        );
      },
    },
    {
      field: 'created_by',
      headerName: 'Created by',
      width: 180,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => (row.created_by || row.source !== 'marketplace' ? userLabel(row.created_by) : inviteSourceLabel(row)),
      renderCell: (p) =>
        !p.row.created_by && p.row.source === 'marketplace' ? (
          // Minted by a booking system's marketplace app install: nobody in ops created it.
          <CellActionsPopover
            auto
            title="Minted by the booking system's marketplace app install"
            actions={[filter('Filter by this source', 'source', 'marketplace')]}
            sx={ellipsis}
          >
            <Box component="span">{inviteSourceLabel(p.row)}</Box>
          </CellActionsPopover>
        ) : p.row.created_by ? (
          <CellActionsPopover
            auto
            actions={[
              p.row.created_by.email
                ? filter('Filter by this creator', 'created_by', p.row.created_by.email)
                : filter('Filter by this creator', 'created_by', String(p.row.created_by.id)),
            ]}
            sx={ellipsis}
          >
            <Box component="span">{userLabel(p.row.created_by)}</Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'created_ts',
      headerName: 'Created',
      width: 160,
      filterable: false,
      renderCell: (p) => <TsValue value={p.row.created_ts} />,
    },
    {
      field: 'expires_ts',
      headerName: 'Expires',
      width: 160,
      filterable: false,
      renderCell: (p) => {
        if (!p.row.expires_ts) return dash;
        const past = new Date(p.row.expires_ts).getTime() < Date.now();
        return (
          <Box component="span" sx={past ? { color: 'error.main' } : undefined}>
            <TsValue value={p.row.expires_ts} />
          </Box>
        );
      },
    },
    {
      field: 'claimed_ts',
      headerName: 'Claim',
      width: 220,
      filterable: false,
      valueGetter: (_v, row) => row.claim.claimed_ts ?? '',
      renderCell: (p) =>
        p.row.claim.claimed && p.row.claim.claimed_ts ? (
          <Box component="span" sx={ellipsis}>
            since <TsValue value={p.row.claim.claimed_ts} />
            {p.row.claim.ip ? (
              <Box component="span" sx={{ ...mono, fontSize: 12, color: 'text.secondary' }}>
                {' '}
                · {p.row.claim.ip}
              </Box>
            ) : null}
          </Box>
        ) : (
          dash
        ),
    },
    {
      field: 'last_activity_ts',
      headerName: 'Last activity',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => (p.row.last_activity_ts ? <TsValue value={p.row.last_activity_ts} /> : dash),
    },
    {
      field: 'sent_ts',
      headerName: 'Sends',
      width: 170,
      filterable: false,
      valueGetter: (_v, row) => row.send_count,
      renderCell: (p) =>
        p.row.send_count > 0 ? (
          <Box component="span" sx={ellipsis}>
            {p.row.send_count} · <TsValue value={p.row.sent_ts} />
          </Box>
        ) : (
          dash
        ),
    },
    {
      field: 'tenant',
      headerName: 'Tenant',
      width: 150,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tenant?.code ?? row.tenant_code ?? '',
      renderCell: (p) =>
        p.row.tenant ? (
          <WithTenantMark environment={p.row.tenant.environment}>
            <CellActionsPopover
              auto
              title={p.row.tenant.name}
              actions={[
                ...(perms.canOpenTenant
                  ? [
                      {
                        key: 'open',
                        label: 'Open tour operator',
                        icon: <OpenInNewIcon fontSize="small" />,
                        href: `/tour-operators/${p.row.tenant.id}`,
                      },
                    ]
                  : []),
                filter('Filter by this tenant', 'tenant', p.row.tenant.code),
              ]}
              sx={ellipsis}
            >
              <Box component="span" sx={mono}>
                {p.row.tenant.code}
              </Box>
            </CellActionsPopover>
          </WithTenantMark>
        ) : (
          dash
        ),
    },
    {
      field: 'public_id',
      headerName: 'Public id',
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <CellActionsPopover auto actions={[copyAction('Copy public id', p.row.public_id)]}>
          <Box component="span" sx={mono}>
            {p.row.public_id}
          </Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'batch_id',
      headerName: 'Batch',
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.batch_id ? (
          <CellActionsPopover
            auto
            title={p.row.batch_id}
            actions={[copyAction('Copy batch id', p.row.batch_id), filter('Filter by this batch', 'batch', p.row.batch_id)]}
          >
            <Box component="span" sx={mono}>
              {p.row.batch_id.slice(0, 8)}…
            </Box>
          </CellActionsPopover>
        ) : (
          dash
        ),
    },
    {
      field: 'used_ts',
      headerName: 'Used',
      width: 160,
      filterable: false,
      renderCell: (p) => (p.row.used_ts ? <TsValue value={p.row.used_ts} /> : dash),
    },
    {
      field: 'completed_ts',
      headerName: 'Went live',
      width: 160,
      filterable: false,
      renderCell: (p) => (p.row.completed_ts ? <TsValue value={p.row.completed_ts} /> : dash),
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      filterable: false,
      renderCell: (p) => (
        <CellActionsPopover
          auto
          title={String(p.row.id)}
          actions={[
            copyAction('Copy invite id', String(p.row.id)),
            { key: 'open', label: 'Open invite', icon: <OpenInNewIcon fontSize="small" />, href: detailPath(p.row.id) },
          ]}
        >
          <Box component="span" sx={mono}>
            {p.row.id}
          </Box>
        </CellActionsPopover>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      hideable: false,
      renderCell: (p) => <InviteActionsMenu invite={p.row} perms={perms} onRun={onRun} />,
    },
  ];
}

/** Hidden until a preset or the column menu shows them. */
export const DEFAULT_HIDDEN = ['batch_id', 'used_ts', 'completed_ts'];
